'use client'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Col,
  Icon,
  type IconNames,
  Row,
  Typography,
} from '@repo/ui/components'
import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { Link, useRouter } from '@repo/shared/i18n/navigation'
import { usePathname } from '@repo/shared/i18n/navigation'

import { useAgentAuthDetails, useLogout } from '@repo/services'
import { cn } from '@repo/ui/lib/utils'
import { Cookie } from '@repo/tools'
import { globalConfig } from '@repo/shared/utils/global-config'

type MenuItem = {
  label: MessagesKeys
  icon: IconNames
  activeIcon?: IconNames
  badge?: {
    text: MessagesKeys
    variant: 'liquid' | 'solid'
    count?: number
    value?: number
    color?: 'negative' | 'positive' | 'neutral' | 'alert'
  }
  href: string
  disabled: boolean
}

export const DashboardAgentsSidebarLayout = () => {
  const { data: authDetails } = useAgentAuthDetails()

  const { push } = useRouter()

  const menuItems: MenuItem[] = [
    {
      label: 'dashboardAgent_menu_dashboard',
      icon: 'GridRaged',
      href: '/dashboard',
      disabled: false,
    },

    {
      label: 'dashboardAgent_menu_projects',
      icon: 'House7',
      href: '/projects',
      disabled: false,
    },
    {
      label: 'dashboardAgent_menu_messages',
      icon: 'EnvelopeClose',
      href: '/messages',
      disabled: false,
    },
    {
      label: 'dashboardAgent_menu_history',
      icon: 'ClockHistoryCircle',
      href: '/history',
      disabled: true,
    },
    {
      label: 'dashboardAgent_menu_wallet',
      icon: 'Wallet',
      href: '/wallet',
      disabled: false,
    },
    {
      label: 'dashboardAgent_menu_settings',
      icon: 'Gear',
      href: '/profile',
      disabled: false,
    },
    // {
    //   label: 'dashboardAgent_menu_referral',
    //   icon: 'Gift',
    //   href: '/referral',
    //   disabled: true,
    // },
  ]

  const { t } = useTranslate()
  const pathname = usePathname()
  const { clearCache } = useLogout()

  const handleLogout = () => {
    Cookie.removeAuthTokens()
    clearCache()
    push('/auth/login')
  }

  return (
    <Col className='gap-md justify-between bg-neutral-surface-dark h-full p-md'>
      <Col>
        <Col className='gap-md items-center p-md border-b border-neutral-surface-5'>
          <Avatar className='h-14 w-14'>
            <AvatarImage
              src={
                authDetails?.data?.details?.photo?.largeURL
                  ? `${globalConfig.baseImageUrl}${authDetails?.data?.details?.photo?.largeURL}`
                  : '/images/placeholder.webp'
              }
              alt='User'
            />
            <AvatarFallback>
              {(authDetails?.data?.details?.firstName || '')
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
          <Typography size='sm' weight='bold'>
            {authDetails?.data?.details?.firstName}{' '}
            {authDetails?.data?.details?.lastName}
          </Typography>
        </Col>

        <Col className='gap-md'>
          {menuItems.map((item, index) => {
            const isActive = pathname?.includes(item.href)
            return (
              <Row
                key={index}
                as={Link}
                href={item.href}
                className={cn(
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed pointer-events-none '
                    : '',
                  'py-md ltr:pl-md rtl:pr-md',
                )}
              >
                <Row className='py-2xs gap-md'>
                  <Icon name={item.icon} size='sm' />
                  <Typography size='sm' weight='bold'>
                    {t(item.label)}
                  </Typography>
                </Row>
                {item.badge &&
                  (item.badge.count !== undefined
                    ? item.badge?.count > 0
                    : true) && (
                    <Badge
                      size='sm'
                      variant={item.badge.variant}
                      color={
                        item.badge.variant === 'solid' ? 'negative' : undefined
                      }
                    >
                      {item.badge.count && `${item.badge.count} `}
                      {t(item.badge.text)}
                      {item.badge.value && `${item.badge.value} `}
                    </Badge>
                  )}
                {isActive && (
                  <Icon
                    name='ChevronLeftLine'
                    size='sm'
                    color='fill-neutral-surface-20'
                  />
                )}
              </Row>
            )
          })}
        </Col>
      </Col>

      <Col>
        <Button
          width='full'
          variant='transparent'
          startIcon={<Icon name='ExitRightLine' size='sm' />}
          onClick={handleLogout}
          color='neutral'
          className='text-sm'
        >
          {t('common_logout')}
        </Button>
        <Row className='justify-center gap-md mt-md'>
          <Typography
            weight='regular'
            size='2xs'
            color='text-neutral-surface-60'
          >
            {t('profileLayout_version')} ۰.۰.۰.۰
          </Typography>
        </Row>
      </Col>
    </Col>
  )
}
